import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../../components/header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import CtaSection from "../../components/CtaSection/CtaSection";
import aImg from "../../images/about/Career_result.webp";
import aImg2 from "../../images/about/Career-vertical_result.webp";
import axios from "axios";
import ModalVideo from "react-modal-video";
import "./career.scss";
import { useSeo } from "../../hooks/useSeo";

const normalizeJob = (raw) => {
  return {
    id: (raw._id && (raw._id.$oid || raw._id)) || raw.id || raw._id,
    title: raw.title || "",
    department: raw.department || "",
    location: raw.location || "",
    jobType: raw.jobType || raw.type || "",
    experience: raw.experience || "",
    responsibilities: Array.isArray(raw.responsibilities) ? raw.responsibilities : [],
    requirements: Array.isArray(raw.requirements) ? raw.requirements : [],
    status: raw.status || "",
    postedAt:
      (raw.postedDate && (raw.postedDate.$date || raw.postedDate)) ||
      raw.createdAt?.$date ||
      raw.createdAt ||
      null,
    description:
      raw.description ||
      "Learn more about this opportunity by exploring responsibilities and requirements below.",
  };
};

const staticMeta = {
  title: "Careers at RashTech Info",
  description: "Explore open roles at RashTech Info. Join cross‑functional teams building high‑impact products.",
  url: "https://rashtechinfo.com/careers",
  image: "https://rashtechinfo.com/og-image.png",
  siteName: "RashTech Info",
  keywords: "careers, jobs, hiring, openings, RashTech Info",
  themeColor: "#0A0D1C",
};

const CareerPage = () => {
  const [isOpen, setOpen] = useState(false);

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Filters and search
  const [query, setQuery] = useState("");
  const [dept, setDept] = useState("All");
  const [location, setLocation] = useState("All");
  const [jobType, setJobType] = useState("All");

  // Expanded job details
  const [expandedId, setExpandedId] = useState(null);

  // Apply modal
  const [applyOpen, setApplyOpen] = useState(false);
  const [activeJob, setActiveJob] = useState(null);

  // Application form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    portfolio: "",
    linkedIn: "",
    findWhere: "",
    resumeAttachment: null, // File
  });
  const [appSubmitting, setAppSubmitting] = useState(false);
  const [appFeedback, setAppFeedback] = useState("");

  // SEO
  const { seo } = useSeo("/getSeo");
  const computed = useMemo(() => {
    const dynamic = {
      title: seo?.title,
      description: seo?.metaDescription,
      keywords: seo?.metaKeywords,
      canonicalUrl: seo?.canonicalUrl,
    };
    return {
      title: dynamic.title || staticMeta.title,
      description: dynamic.description || staticMeta.description,
      keywords: dynamic.keywords || staticMeta.keywords,
      url: dynamic.canonicalUrl || staticMeta.url,
      image: staticMeta.image,
      siteName: staticMeta.siteName,
      themeColor: staticMeta.themeColor,
    };
  }, [seo]);

  useEffect(() => {
    let mounted = true;
    const fetchJobs = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const { data } = await axios.get("https://api.rashtechinfo.com/GetOpen_Careers", {
          headers: { Accept: "application/json" },
        });
        const job = data.data;
        const normalized = Array.isArray(job) ? job.map(normalizeJob) : [];
        if (mounted) setJobs(normalized);
      } catch (e) {
        if (mounted) setLoadError("Unable to load openings at the moment.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchJobs();
    return () => {
      mounted = false;
    };
  }, []);

  const departments = useMemo(() => {
    const setVals = new Set(jobs.map((j) => j.department).filter(Boolean));
    return ["All", ...Array.from(setVals)];
  }, [jobs]);

  const locations = useMemo(() => {
    const setVals = new Set(jobs.map((j) => j.location).filter(Boolean));
    return ["All", ...Array.from(setVals)];
  }, [jobs]);

  const jobTypes = useMemo(() => {
    const setVals = new Set(jobs.map((j) => j.jobType).filter(Boolean));
    return ["All", ...Array.from(setVals)];
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return jobs.filter((j) => {
      const matchesQuery =
        !q ||
        j.title.toLowerCase().includes(q) ||
        j.department.toLowerCase().includes(q) ||
        j.location.toLowerCase().includes(q) ||
        j.jobType.toLowerCase().includes(q) ||
        j.description.toLowerCase().includes(q);

      const matchesDept = dept === "All" || j.department === dept;
      const matchesLoc = location === "All" || j.location === location;
      const matchesType = jobType === "All" || j.jobType === jobType;

      return (
        matchesQuery &&
        matchesDept &&
        matchesLoc &&
        matchesType &&
        (j.status || "").toLowerCase() === "open"
      );
    });
  }, [jobs, query, dept, location, jobType]);

  const formatDate = (value) => {
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const openApply = (job) => {
    setActiveJob(job);
    setApplyOpen(true);
    setAppFeedback("");
    setAppSubmitting(false);
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      portfolio: "",
      linkedIn: "",
      findWhere: "",
      resumeAttachment: null,
    });
  };

  const closeApply = () => {
    setApplyOpen(false);
    setActiveJob(null);
  };

  const onChangeField = (e) => {
    const { name, value, files } = e.target;
    if (name === "resumeAttachment") {
      setFormData((prev) => ({
        ...prev,
        resumeAttachment: files && files[0] ? files[0] : null,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!activeJob) return;

    if (!formData.fullName || !formData.email) {
      setAppFeedback("Please add your full name and a valid email.");
      return;
    }

    const submitData = new FormData();
    submitData.append("jobId", activeJob.id);
    submitData.append("fullName", formData.fullName);
    submitData.append("email", formData.email);
    submitData.append("phone", formData.phone || "");
    submitData.append("portfolio", formData.portfolio || "");
    submitData.append("linkedIn", formData.linkedIn || "");
    submitData.append("findWhere", formData.findWhere || "");
    if (formData.resumeAttachment) {
      submitData.append("resumeAttachment", formData.resumeAttachment);
    }

    try {
      setAppSubmitting(true);
      setAppFeedback("");
      await axios.post("https://api.rashtechinfo.com/applyForJob", submitData, {
        headers: { Accept: "application/json" },
      });
      setAppFeedback("Application submitted successfully! We’ll get back soon.");
      setTimeout(() => closeApply(), 1500);
    } catch (err) {
      setAppFeedback("Failed to submit. Please try again later.");
    } finally {
      setAppSubmitting(false);
    }
  };

  const ClickHandler = () => {
    window.scrollTo(10, 0);
  };

  return (
    <Fragment>
      <Helmet>
        <title>{computed.title}</title>
        <meta name="description" content={computed.description} />
        {computed.keywords && <meta name="keywords" content={computed.keywords} />}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={computed.themeColor} />
        {computed.url && <link rel="canonical" href={computed.url} />}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={computed.siteName} />
        <meta property="og:title" content={computed.title} />
        <meta property="og:description" content={computed.description} />
        {computed.url && <meta property="og:url" content={computed.url} />}
        {computed.image && <meta property="og:image" content={computed.image} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={computed.title} />
        <meta name="twitter:description" content={computed.description} />
        {computed.image && <meta name="twitter:image" content={computed.image} />}
      </Helmet>

      <Header />
      <main className="page_content careers-page">
        <PageTitle
          pageTitle={"Career"}
          pagesub={"Life at RashTech Info"}
          pageTop={"Careers"}
        />

        {/* Intro + video */}
        <section className="intro_about_section section_space bg-light">
          <div className="intro_about_content">
            <div className="container">
              <div className="row">
                <div className="col-lg-8">
                  <div className="image_wrap">
                    <img src={aImg} alt="RashTech Info - Careers" />
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="image_wrap position-relative">
                    <img src={aImg2} alt="RashTech Info - Culture" />
                    <button className="video_btn ripple_effect" onClick={() => setOpen(true)}>
                      <span className="btn_icon">
                        <i className="fa-solid fa-play"></i>
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="heading_block mt-4">
                <div className="row justify-content-lg-between">
                  <div className="col-lg-4">
                    <div className="heading_focus_text">
                      Join our team
                      <span className="badge bg-secondary text-white ms-2">RashTech Info</span>
                    </div>
                    <h2 className="heading_text mb-0">Build with impact</h2>
                  </div>
                  <div className="col-lg-6">
                    <p className="heading_description mb-0">
                      At RashTech Info, we bring together cross-functional squads to deliver pragmatic solutions in AI, cloud, data, and product engineering. Here, your work creates real impact for businesses and users, while you grow with the right mentorship, flexibility, and opportunities.
                    </p>
                  </div>
                </div>
              </div>

              {/* Why work with us */}
              <div className="why-us mt-5 row g-4">
                <div className="col-md-4">
                  <div className="why-card">
                    <h5>Flexible by design</h5>
                    <p>Hybrid & remote options, async collaboration, outcomes over hours.</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="why-card">
                    <h5>Learn & grow</h5>
                    <p>Mentorship, L&D budget, tech talks, and clear growth paths.</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="why-card">
                    <h5>Impact that matters</h5>
                    <p>Ship value fast, own outcomes end‑to‑end, celebrate results.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filters + openings */}
        <section className="section_space">
          <div className="container">
            <div className="heading_block">
              <div className="row justify-content-lg-between align-items-end">
                <div className="col-lg-6">
                  <h3 className="heading_text mb-2">Open positions</h3>
                  <p className="heading_description mb-0">
                    Explore roles across engineering, delivery, and data. Don’t see a fit? Send a general application.
                  </p>
                </div>
                <div className="col-lg-6">
                  <div className="row g-2">
                    <div className="col-12 col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search title, skills, location..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                      />
                    </div>
                    <div className="col-4 col-md-3">
                      <select className="form-select" value={dept} onChange={(e) => setDept(e.target.value)}>
                        {departments.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-4 col-md-3">
                      <select className="form-select" value={location} onChange={(e) => setLocation(e.target.value)}>
                        {locations.map((l) => (
                          <option key={l} value={l}>{l}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-4 col-md-2">
                      <select className="form-select" value={jobType} onChange={(e) => setJobType(e.target.value)}>
                        {jobTypes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Jobs list */}
            <div className="mt-4">
              {loading && <div className="text-muted">Loading openings...</div>}
              {!loading && loadError && <div className="alert alert-warning">{loadError}</div>}
              {!loading && !loadError && filteredJobs.length === 0 && (
                <div className="text-muted">No openings match the current filters.</div>
              )}

              <div className="row g-2">
                {filteredJobs.map((job) => {
                  const isOpenCard = expandedId === job.id;
                  return (
                    <div className="col-12" key={job.id}>
                      <article className="job-card">
                        <div className="job-card__head">
                          <div className="job-card__title">
                            <h5>{job.title}</h5>
                            <div className="meta">
                              {!!job.department && (
                                <span><i className="fa-solid fa-layer-group me-1" />{job.department}</span>
                              )}
                              {!!job.location && (
                                <span><i className="fa-solid fa-location-dot me-1" />{job.location}</span>
                              )}
                              {!!job.jobType && (
                                <span><i className="fa-regular fa-clock me-1" />{job.jobType}</span>
                              )}
                              {!!job.experience && (
                                <span><i className="fa-solid fa-briefcase me-1" />{job.experience}</span>
                              )}
                              {!!job.postedAt && (
                                <span><i className="fa-regular fa-calendar me-1" />Posted {formatDate(job.postedAt)}</span>
                              )}
                            </div>
                          </div>

                          <div className="job-card__actions">
                            <button
                              className="career_button"
                              onClick={() => setExpandedId(isOpenCard ? null : job.id)}
                            >
                              {isOpenCard ? "Hide details" : "View details"}
                            </button>
                            <button className="career_button" onClick={() => openApply(job)}>
                              Apply now
                            </button>
                          </div>
                        </div>

                        {isOpenCard && (
                          <div className="job-card__body">
                            {job.description && <p className="mb-3">{job.description}</p>}

                            {job.responsibilities.length > 0 && (
                              <div className="job-section">
                                <h6>Responsibilities</h6>
                                <ul>
                                  {job.responsibilities.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {job.requirements.length > 0 && (
                              <div className="job-section">
                                <h6>Requirements</h6>
                                <ul>
                                  {job.requirements.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </article>
                    </div>
                  );
                })}
              </div>

            </div>
          </div>
        </section>
      </main>

      <CtaSection />
      <Footer />
      <Scrollbar />
      <ModalVideo
        channel="youtube"
        autoplay
        isOpen={isOpen}
        videoId="q1wQjhQpVJQ"
        onClose={() => setOpen(false)}
      />

      {/* Apply Modal */}
      {applyOpen && (
  <div className="apply-modal" role="dialog" aria-modal="true">
    <div className="apply-modal__dialog">
      <div className="apply-modal__head">
        <div>
          <h5 className="mb-1">Apply: {activeJob?.title}</h5>
          <div className="small text-muted">
            {activeJob?.department} • {activeJob?.location}
          </div>
        </div>
        <button className="career_close_button" onClick={closeApply} aria-label="Close" title="Close">
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>

      <form className="apply-form" onSubmit={submitApplication}>
        <div className="apply-form__grid">
          <div className="apply-form__group">
            <label className="form-label">Full name *</label>
            <input
              type="text"
              name="fullName"
              className="form-control"
              value={formData.fullName}
              onChange={onChangeField}
              placeholder="Enter Your Name"
              required
            />
          </div>
          <div className="apply-form__group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={onChangeField}
              placeholder="Enter Your Email Address"
              required
            />
          </div>
          <div className="apply-form__group">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              name="phone"
              className="form-control"
              value={formData.phone}
              onChange={onChangeField}
              placeholder="Enter Your Personal Number"
            />
          </div>
          <div className="apply-form__group">
            <label className="form-label">LinkedIn</label>
            <input
              type="url"
              name="linkedIn"
              className="form-control"
              value={formData.linkedIn}
              onChange={onChangeField}
              placeholder="Enter Your LinkedIn Profile"
            />
          </div>
          <div className="apply-form__group">
            <label className="form-label">Portfolio</label>
            <input
              type="url"
              name="portfolio"
              className="form-control"
              value={formData.portfolio}
              onChange={onChangeField}
              placeholder="Enter Your Portfolio Link"
            />
          </div>
          <div className="apply-form__group">
            <label className="form-label">How did you find us?</label>
            <select
              name="findWhere"
              className="form-select"
              value={formData.findWhere}
              onChange={onChangeField}
            >
              <option value="">Select</option>
              <option value="LinkedIn">LinkedIn</option>
              <option value="Naukri">Naukri</option>
              <option value="Indeed">Indeed</option>
              <option value="Referral">Referral</option>
              <option value="Company Website">Company Website</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="apply-form__group apply-form__group--full">
            <label className="form-label">Resume (PDF/DOC) *</label>
            <div className="file-input-container">
              <input
                type="file"
                name="resumeAttachment"
                id="resumeAttachment"
                className="file-input"
                accept=".pdf,.doc,.docx"
                onChange={onChangeField}
                required
              />
              <label htmlFor="resumeAttachment" className="file-input-label">
                <i className="fa-solid fa-upload"></i>
                {formData.resumeAttachment ? formData.resumeAttachment.name : "Choose file"}
              </label>
            </div>
            <div className="form-text">Max 5MB. Accepted: PDF, DOC, DOCX.</div>
          </div>
        </div>

        {appFeedback && (
          <div
            className={`alert mt-3 ${
              appFeedback.startsWith("Application submitted") ? "alert-success" : "alert-warning"
            }`}
          >
            {appFeedback}
          </div>
        )}

        <div className="apply-modal__actions">
          <button type="button" className="career_button career_button--outline" onClick={closeApply} disabled={appSubmitting}>
            Cancel
          </button>
          <button type="submit" className="career_button" disabled={appSubmitting}>
            {appSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}
    </Fragment>
  );
};

export default CareerPage;
